import { Footer, Layout } from 'components';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import {
  AddminAddTransaction,
  AdminCalculatePayments,
  AdminCostAdjustment,
  AdminEditEquipmentGroups,
  AdminEditPaymentHistory,
  AdminEditUsers,
  AdminEditPoolConfigs,
  AdminEditGroupPoolConfigs,
  AdminEditDeviceGroups,
} from './components';
import { Styledadmin } from './styles';
import { useEffect, useRef, useState, useCallback } from 'react';

import { ReactComponent as ArrowRightIcon } from 'assets/icons/arrow-right-sharp.svg';
import { useAppDispatch, useAppSelector } from 'store/store';
import { fetchCosts } from 'store/thunks/adminCostThunk';
import { DEFAULT_TIMEOUT_MS, GET_POOL_CONFIGS_TIMEOUT_MS } from 'constants/appConstants';
import {
  VALIDATION_ERROR_ADMIN_YESTERDAY_COST_REQUIRED,
  VALIDATION_ERROR_ADMIN_NEWBIE_COST_REQUIRED,
  VALIDATION_ERROR_ADMIN_NEWBIE_ADJUST_REQUIRED,
} from 'constants/errorsConstants';
import { AdminRepository } from 'repositories/AdminRepository';
import { EApiResponseStatus } from 'types/api';
import { fetchUsers } from 'store/thunks/adminUsersThunk';
import { fetchPaymentHistory } from 'store/thunks/adminPaymentThunks';
import { fetchDeviceGroups } from 'store/thunks/adminDeviceGroupsThunk';
import { fetchEquipmentGroups } from 'store/thunks/adminEquipmentGroupsThunk';
import { fetchPoolConfigs } from 'store/thunks/adminPoolConfigsThunk';
import {fetchGroupPoolConfigsAll} from '../../store/thunks/adminGroupPoolConfigsThunk';

export const AdminPage = () => {
  const [activeTabId, setActiveTabId] = useState(0);
  const [scrollPosition, setScrollPosition] = useState<'start' | 'end'>('start');
  const { yesterdayCost, newbieCost, newbieAdjust } = useAppSelector((state) => state.adminCosts);
  const { token } = useAppSelector((state) => state.auth);
  const { t } = useTranslation();
  const tabsHeaderRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const timeoutCosts = getCosts();
    const timeoutUsers = getUsersList();
    const timeoutPaymentHistory = getPaymentHistoryList();
    const timeoutDeviceGroups = getDeviceGroupsList();
    const timeoutEquipmentGroups = getEquipmentGroupsList();
    const timeoutPoolConfigs = getPoolConfigsList();

    return () => {
      clearTimeout(timeoutCosts);
      clearTimeout(timeoutUsers);
      clearTimeout(timeoutPaymentHistory);
      clearTimeout(timeoutDeviceGroups);
      clearTimeout(timeoutEquipmentGroups);
      clearTimeout(timeoutPoolConfigs);
    };
  }, []);

  const getCosts = () => {
    dispatch(fetchCosts());

    return setTimeout(() => {
      getCosts();
    }, DEFAULT_TIMEOUT_MS);
  };

  const getDeviceGroupsList = () => {
    dispatch(fetchDeviceGroups());

    return setTimeout(() => {
      getDeviceGroupsList();
    }, DEFAULT_TIMEOUT_MS);
  };

  const getUsersList = () => {
    dispatch(fetchUsers());

    return setTimeout(() => {
      getUsersList();
    }, DEFAULT_TIMEOUT_MS);
  };

  const getEquipmentGroupsList = () => {
    dispatch(fetchEquipmentGroups());

    return setTimeout(() => {
      getEquipmentGroupsList();
    }, DEFAULT_TIMEOUT_MS);
  };

  const getPaymentHistoryList = () => {
    dispatch(fetchPaymentHistory());

    return setTimeout(() => {
      getPaymentHistoryList();
    }, DEFAULT_TIMEOUT_MS);
  };

  const getPoolConfigsList = () => {
    dispatch(fetchPoolConfigs());
    dispatch(fetchGroupPoolConfigsAll());
    return setTimeout(() => {
      getPoolConfigsList();
    }, GET_POOL_CONFIGS_TIMEOUT_MS * 10);
  };

  const submitYesterdayCostAdjustment = useCallback(
    async (amount: number) => {
      if (amount === yesterdayCost) {
        return;
      }

      try {
        if (!token) throw Error('no token or unathorised');
        if (!amount) throw Error(VALIDATION_ERROR_ADMIN_YESTERDAY_COST_REQUIRED);
        if (!newbieCost) throw Error(VALIDATION_ERROR_ADMIN_NEWBIE_COST_REQUIRED);
        if (!newbieAdjust) throw Error(VALIDATION_ERROR_ADMIN_NEWBIE_ADJUST_REQUIRED);

        const response = await AdminRepository.setYesterdayCost(token, {
          yesterdayCost: amount,
          newbieCost,
          newbieAdjust,
        });
        if (response.status === EApiResponseStatus.FAILURE) {
          throw Error(response.error);
        }

        await dispatch(fetchCosts());
      } catch (error) {
        console.error('submit yesterday cost error', error);
      }
    },
    [yesterdayCost, newbieCost, newbieAdjust, token, dispatch],
  );

  const submitNewbeeCostAdjustment = useCallback(
    async (amount: number) => {
      if (amount === newbieCost) {
        return;
      }

      try {
        if (!token) throw Error('no token or unathorised');
        if (!amount) throw Error(VALIDATION_ERROR_ADMIN_NEWBIE_COST_REQUIRED);

        const response = await AdminRepository.setNewbieCost(token, { newbieCost: amount });
        if (response.status === EApiResponseStatus.FAILURE) {
          throw Error(response.error);
        }

        await dispatch(fetchCosts());
      } catch (error) {
        console.error('submit newbie cost error', error);
      }
    },
    [newbieCost, token, dispatch],
  );

  const submitEditNewbeeAdjustment = useCallback(
    async (amount: number) => {
      if (amount === newbieAdjust) {
        return;
      }

      try {
        if (!token) throw Error('no token or unathorised');
        if (!amount) throw Error(VALIDATION_ERROR_ADMIN_NEWBIE_ADJUST_REQUIRED);

        const response = await AdminRepository.setNewbieAdjust(token, { newbieAdjust: amount });
        if (response.status === EApiResponseStatus.FAILURE) {
          throw Error(response.error);
        }

        await dispatch(fetchCosts());
      } catch (error) {
        console.error('submit newbie adjust error', error);
      }
    },
    [newbieAdjust, token, dispatch],
  );

  const MAX_TABS_QUANTITY = 6;

  const switchTabHandlerLeft = () => setActiveTabId((prev) => (prev < 1 ? 0 : prev - 1));
  const switchTabHandlerRight = () =>
    setActiveTabId((prev) => (prev > MAX_TABS_QUANTITY - 2 ? MAX_TABS_QUANTITY - 1 : prev + 1));

  const switchTabHandlerSwitch = () => {
    console.log('switch cleck');
    if (tabsHeaderRef.current) {
      if (scrollPosition === 'start') {
        tabsHeaderRef.current.scrollLeft += tabsHeaderRef.current.clientWidth;
        setScrollPosition('end');
      } else {
        tabsHeaderRef.current.scrollLeft = 0;
        setScrollPosition('start');
      }
    }
  };

  return (
    <Layout admin>
      <Styledadmin>
        <div className="admin-controls">
          <AdminCalculatePayments />
          <AddminAddTransaction />
          <AdminCostAdjustment
            onSubmit={submitYesterdayCostAdjustment}
            heading={t('adminPage.yesterdayCost.heading')}
            cost={yesterdayCost || 0}
          />
          <AdminCostAdjustment
            onSubmit={submitNewbeeCostAdjustment}
            heading={t('adminPage.newbeeCost.heading')}
            cost={newbieCost || 0}
          />
          <AdminCostAdjustment
            onSubmit={submitEditNewbeeAdjustment}
            heading={t('adminPage.editNewbeeAdjust.heading')}
            cost={newbieAdjust || 0}
          />
        </div>
        <div className="admin-tabs">
          <div className="admin-tabs-header">
            <div className="admin-tabs-headers" ref={tabsHeaderRef}>
              <div
                className={cn('admin-tabs-header__container', `l-${activeTabId * 100}`)}
                data-background="lime"
              >
                <button
                  className={cn('admin-tabs-header__tab', activeTabId === 0 && 'active')}
                  onClick={() => setActiveTabId(0)}
                >
                  <div className="admin-tabs-header__tab__title">
                    {t('adminPage.editDeviceGroups.tabHeader')}
                  </div>
                </button>
                <button
                  className={cn('admin-tabs-header__tab', activeTabId === 1 && 'active')}
                  onClick={() => setActiveTabId(1)}
                >
                  <div className="admin-tabs-header__tab__title">
                    {t('adminPage.editUsers.tabHeader')}
                  </div>
                </button>
                <button
                  className={cn('admin-tabs-header__tab', activeTabId === 2 && 'active')}
                  onClick={() => setActiveTabId(2)}
                >
                  <div className="admin-tabs-header__tab__title">
                    {t('adminPage.editEquipmentGroups.tabHeader')}
                  </div>
                </button>
                <button
                  className={cn('admin-tabs-header__tab', activeTabId === 3 && 'active')}
                  onClick={() => setActiveTabId(3)}
                >
                  <div className="admin-tabs-header__tab__title">
                    {t('adminPage.editPaymentHistory.tabHeader')}
                  </div>
                </button>
                <button
                  className={cn('admin-tabs-header__tab', activeTabId === 4 && 'active')}
                  onClick={() => setActiveTabId(4)}
                >
                  <div className="admin-tabs-header__tab__title">
                    {t('adminPage.editPoolConfigs.tabHeader')}
                  </div>
                </button>
                <button
                  className={cn('admin-tabs-header__tab', activeTabId === 5 && 'active')}
                  onClick={() => setActiveTabId(5)}
                >
                  <div className="admin-tabs-header__tab__title">
                    {t('adminPage.editGroupPoolConfigs.tabHeader')}
                  </div>
                </button>
              </div>
            </div>
            <button className="admin-tabs-header-button left" onClick={switchTabHandlerLeft}>
              <ArrowRightIcon className="icon" />
            </button>
            <button className="admin-tabs-header-button right" onClick={switchTabHandlerRight}>
              <ArrowRightIcon className="icon" />
            </button>
            <button
              className={cn('admin-tabs-header-button switch', scrollPosition)}
              onClick={switchTabHandlerSwitch}
            >
              <ArrowRightIcon className="icon" />
            </button>
          </div>
          <div className={cn('admin-tabs-content', 'active')}>
            {activeTabId === 0 && <AdminEditDeviceGroups />}
            {activeTabId === 1 && <AdminEditUsers />}
            {activeTabId === 2 && <AdminEditEquipmentGroups />}
            {activeTabId === 3 && <AdminEditPaymentHistory />}
            {activeTabId === 4 && <AdminEditPoolConfigs />}
            {activeTabId === 5 && <AdminEditGroupPoolConfigs />}
          </div>
        </div>
        <Footer smaller />
      </Styledadmin>
    </Layout>
  );
};
